import React, { useState } from "react";
import { api, API } from "@/lib/api";
import { Upload, X, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_CSV = `supplier_name,supplier_email,supplier_type,website,country_code,state_code,city,country,contact_phone,categories,title,description,price_min,price_max,currency,image_url,lead_time,moq,tags
Acme Linens Ltd,sales@acme.com,Manufacturer,https://acme.com,GB,,London,United Kingdom,+44-20-1234-5678,"Bath Linen,Bed Linen",Egyptian Cotton Bath Towel,600 gsm luxury towel,18.00,32.00,USD,,4 weeks,250,cotton|oeko-tex|premium
Coastal Textile Co.,sales@coastal.example,Wholesaler,https://coastal.example,AU,NSW,Sydney,Australia,+61-2-5550-0100,Pool & Spa,Poolside Cabana Towel,Heavyweight jacquard pool towel,22.00,34.00,AUD,,5 weeks,200,pool|cabana|jacquard`;

export default function BulkUploadModal({ open, onClose, onDone }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(f);
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/listings/bulk/template`, { credentials: "include" });
      const csvText = await res.text();
      const blob = new Blob([csvText], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "accommodation-suppliers-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Fallback to bundled sample if backend unreachable
      const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "accommodation-suppliers-template.csv"; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const submit = async () => {
    if (!csv.trim()) { toast.error("Paste CSV or upload a file"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/listings/bulk", { csv_text: csv });
      setResult(data);
      toast.success(`Created ${data.created} listings`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-upload-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8ECE9]">
          <div>
            <div className="tracking-overline mb-1">Bulk import</div>
            <div className="font-serif text-xl text-[#11211A]">Upload CSV of listings</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#7D8C85]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-[#F0EFEA] p-4 text-sm text-[#4A5550]">
            <div className="font-medium text-[#11211A] mb-2">Required columns</div>
            <code className="text-xs">title, description, categories</code>
            <div className="font-medium text-[#11211A] mt-3 mb-1">Supplier fields (used to build supplier profile)</div>
            <code className="text-xs">supplier_name, supplier_email, supplier_type (Manufacturer|Wholesaler), website, country_code, state_code</code>
            <div className="font-medium text-[#11211A] mt-3 mb-1">Product fields (optional)</div>
            <code className="text-xs">price_min, price_max, currency, city, country, contact_phone, image_url, lead_time, moq, tags</code>
            <div className="text-xs text-[#7D8C85] mt-3 leading-relaxed">
              <strong>Multi-category:</strong> put more than one category name separated by commas inside a quoted cell — e.g. <code>"Bath Linen,Bed Linen"</code>.<br/>
              <strong>state_code:</strong> state/province/region code, e.g. <code>NSW, QLD, ON, BC, CA, NY</code> — buyers can filter by this.<br/>
              <strong>Tags:</strong> pipe-separated (<code>cotton|oeko-tex|premium</code>).<br/>
              Missing suppliers are created as unclaimed profiles keyed off <code>supplier_email</code>.
            </div>
            <div className="mt-3">
              <button onClick={downloadTemplate} className="text-[#264839] text-xs inline-flex items-center gap-1 hover:underline" data-testid="download-template">
                <Download className="w-3 h-3" /> Download sample template
              </button>
            </div>
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-[#264839] hover:underline">
              <FileText className="w-4 h-4" /> Choose CSV file
              <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid="bulk-file-input" />
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#4A5550] mb-1">Or paste CSV directly</label>
            <textarea rows={10} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={SAMPLE_CSV} className="w-full px-3 py-2 text-xs font-mono border border-[#C4CFC9] rounded-sm" data-testid="bulk-csv-text" />
          </div>

          {result && (
            <div className="border border-[#E8ECE9] p-4 text-sm">
              <div className="text-[#11211A] font-medium">Import complete</div>
              <div className="text-[#4A5550] mt-1">Created: <span className="text-[#264839] font-medium">{result.created}</span> listings · New supplier profiles: <span className="text-[#264839] font-medium">{result.suppliers_created ?? 0}</span> · Errors: {result.errors.length}</div>
              {result.errors.length > 0 && (
                <ul className="mt-2 text-xs text-red-600 space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="btn-ghost">Close</button>
            <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="bulk-upload-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? "Uploading…" : "Import"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
